import { globalMaxUploadSize } from 'globalConstants';
import ResourceLink from 'models/resourceLink';
import { ResourceListType } from 'models/resourceList';

const libraryDocumentDragMimeType = 'application/x-isoplanner-copilot-library-document';

export enum CopilotMarkdownExtractorSourceType {
  Html = 1,
  Word = 2,
  Pdf = 3,
  Excel = 4,
  PowerPoint = 5,
  Text = 6,
}

export type CopilotDocumentDropValidationMessageKey =
  | 'DropAccept'
  | 'DropUnavailable'
  | 'DropInvalid'
  | 'DropUploadAccept'
  | 'DropUploadMultiple'
  | 'DropUploadTooLarge'
  | 'DropUploadUnsupported';

export type CopilotDocumentDropValidationResult = {
  handled: boolean;
  accepted: boolean;
  messageKey?: CopilotDocumentDropValidationMessageKey;
};

export type CopilotLibraryDocumentDragPayload = {
  linkId: number;
  listId: number;
  listType: ResourceListType;
  linkName: string;
  fileName?: string;
};

let currentLibraryDocumentDragPayload: CopilotLibraryDocumentDragPayload | undefined;

const copilotMarkdownExtractorMimeTypes: Record<string, CopilotMarkdownExtractorSourceType> = {
  'application/pdf': CopilotMarkdownExtractorSourceType.Pdf,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': CopilotMarkdownExtractorSourceType.Word,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template': CopilotMarkdownExtractorSourceType.Word,
  'application/vnd.ms-word.document.macroEnabled.12': CopilotMarkdownExtractorSourceType.Word,
  'application/vnd.ms-word.template.macroEnabled.12': CopilotMarkdownExtractorSourceType.Word,
  'application/msword': CopilotMarkdownExtractorSourceType.Word,
  'application/vnd.ms-excel': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.ms-excel.sheet.macroEnabled.12': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.ms-excel.template.macroEnabled.12': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.ms-excel.addin.macroEnabled.12': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': CopilotMarkdownExtractorSourceType.Excel,
  'application/vnd.ms-powerpoint': CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.openxmlformats-officedocument.presentationml.template':
    CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow':
    CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.ms-powerpoint.addin.macroEnabled.12': CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12': CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.ms-powerpoint.template.macroEnabled.12': CopilotMarkdownExtractorSourceType.PowerPoint,
  'application/vnd.ms-powerpoint.slideshow.macroEnabled.12': CopilotMarkdownExtractorSourceType.PowerPoint,
  'text/html': CopilotMarkdownExtractorSourceType.Html,
  'text/plain': CopilotMarkdownExtractorSourceType.Text,
  'text/markdown': CopilotMarkdownExtractorSourceType.Text,
  'text/x-markdown': CopilotMarkdownExtractorSourceType.Text,
  'application/markdown': CopilotMarkdownExtractorSourceType.Text,
  'application/x-markdown': CopilotMarkdownExtractorSourceType.Text,
};

const copilotMarkdownExtractorExtensionMimeTypes: Record<string, string> = {
  '.asm': 'text/plain',
  '.bas': 'text/plain',
  '.c': 'text/plain',
  '.cc': 'text/plain',
  '.cd': 'text/plain',
  '.cnf': 'text/plain',
  '.cpp': 'text/plain',
  '.cs': 'text/plain',
  '.csdproj': 'text/plain',
  '.csproj': 'text/plain',
  '.cxx': 'text/plain',
  '.dbproj': 'text/plain',
  '.def': 'text/plain',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.dot': 'application/msword',
  '.dotx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  '.docm': 'application/vnd.ms-word.document.macroEnabled.12',
  '.dotm': 'application/vnd.ms-word.template.macroEnabled.12',
  '.dsw': 'text/plain',
  '.h': 'text/plain',
  '.hpp': 'text/plain',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.hxt': 'text/html',
  '.hxx': 'text/plain',
  '.i': 'text/plain',
  '.idl': 'text/plain',
  '.inc': 'text/plain',
  '.inl': 'text/plain',
  '.ipproj': 'text/plain',
  '.jsxbin': 'text/plain',
  '.log': 'text/plain',
  '.lst': 'text/plain',
  '.mak': 'text/plain',
  '.map': 'text/plain',
  '.markdown': 'text/markdown',
  '.md': 'text/markdown',
  '.mdown': 'text/markdown',
  '.mk': 'text/plain',
  '.odh': 'text/plain',
  '.odl': 'text/plain',
  '.pkgdef': 'text/plain',
  '.pkgundef': 'text/plain',
  '.pot': 'application/vnd.ms-powerpoint',
  '.ppa': 'application/vnd.ms-powerpoint',
  '.pps': 'application/vnd.ms-powerpoint',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pwz': 'application/vnd.ms-powerpoint',
  '.rc': 'text/plain',
  '.rc2': 'text/plain',
  '.rct': 'text/plain',
  '.rgs': 'text/plain',
  '.s': 'text/plain',
  '.shtml': 'text/html',
  '.slk': 'application/vnd.ms-excel',
  '.sln': 'text/plain',
  '.sol': 'text/plain',
  '.sor': 'text/plain',
  '.srf': 'text/plain',
  '.tlh': 'text/plain',
  '.tli': 'text/plain',
  '.txt': 'text/plain',
  '.user': 'text/plain',
  '.vb': 'text/plain',
  '.vbdproj': 'text/plain',
  '.vbproj': 'text/plain',
  '.vcs': 'text/plain',
  '.vddproj': 'text/plain',
  '.vdp': 'text/plain',
  '.vdproj': 'text/plain',
  '.vspscc': 'text/plain',
  '.vsscc': 'text/plain',
  '.vssscc': 'text/plain',
  '.wbk': 'application/msword',
  '.wiz': 'application/msword',
  '.xdr': 'text/plain',
  '.xla': 'application/vnd.ms-excel',
  '.xlc': 'application/vnd.ms-excel',
  '.xld': 'application/vnd.ms-excel',
  '.xlk': 'application/vnd.ms-excel',
  '.xll': 'application/vnd.ms-excel',
  '.xlm': 'application/vnd.ms-excel',
  '.xls': 'application/vnd.ms-excel',
  '.xlt': 'application/vnd.ms-excel',
  '.xlw': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xltx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
  '.xltm': 'application/vnd.ms-excel.template.macroEnabled.12',
  '.xlam': 'application/vnd.ms-excel.addin.macroEnabled.12',
  '.xlsb': 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.potx': 'application/vnd.openxmlformats-officedocument.presentationml.template',
  '.ppsx': 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  '.ppam': 'application/vnd.ms-powerpoint.addin.macroEnabled.12',
  '.pptm': 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
  '.potm': 'application/vnd.ms-powerpoint.template.macroEnabled.12',
  '.ppsm': 'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
};

export const copilotMarkdownExtractorAccept = Object.entries(copilotMarkdownExtractorMimeTypes).reduce(
  (accept, [mimeType]) => ({
    ...accept,
    [mimeType]: Object.entries(copilotMarkdownExtractorExtensionMimeTypes)
      .filter(([, extensionMimeType]) => extensionMimeType === mimeType)
      .map(([extension]) => extension),
  }),
  {} as Record<string, string[]>,
);

export const getCopilotMarkdownExtractorSourceFromMimeType = (
  mimeType: string,
): CopilotMarkdownExtractorSourceType | undefined => {
  const normalizedMimeType = mimeType.trim().toLowerCase();
  if (!normalizedMimeType) return undefined;

  return copilotMarkdownExtractorMimeTypes[normalizedMimeType];
};

export const getCopilotUploadDocumentAcceptValues = (): string[] => {
  return Object.entries(copilotMarkdownExtractorAccept).flatMap(([mimeType, extensions]) => [mimeType, ...extensions]);
};

export const isCopilotLibraryDocumentLink = (link: ResourceLink): boolean => {
  return (
    !link.isWholeList() &&
    !link.list.isVirtual &&
    (link.list.listType === ResourceListType.DocumentLibrary || link.list.listType === ResourceListType.SitePageLibrary)
  );
};

export const createCopilotLibraryDocumentDragPayload = (
  link: ResourceLink,
): CopilotLibraryDocumentDragPayload | undefined => {
  if (!isCopilotLibraryDocumentLink(link)) return undefined;

  return {
    linkId: link.linkId,
    listId: link.listId,
    listType: link.list.listType,
    linkName: link.linkName,
    fileName: link.getFileName(),
  };
};

export const hasCopilotLibraryDocumentDragPayload = (dataTransfer: DataTransfer): boolean => {
  return Array.from(dataTransfer.types).includes(libraryDocumentDragMimeType);
};

const parseCopilotLibraryDocumentDragPayload = (rawPayload: string): CopilotLibraryDocumentDragPayload | undefined => {
  try {
    const payload = JSON.parse(rawPayload) as Partial<CopilotLibraryDocumentDragPayload>;
    if (
      typeof payload.linkId !== 'number' ||
      typeof payload.listId !== 'number' ||
      typeof payload.listType !== 'number' ||
      typeof payload.linkName !== 'string'
    ) {
      return undefined;
    }

    return {
      linkId: payload.linkId,
      listId: payload.listId,
      listType: payload.listType,
      linkName: payload.linkName,
      fileName: typeof payload.fileName === 'string' ? payload.fileName : undefined,
    };
  } catch {
    return undefined;
  }
};

export const setCopilotLibraryDocumentDragPayload = (
  dataTransfer: DataTransfer,
  payload: CopilotLibraryDocumentDragPayload,
): void => {
  currentLibraryDocumentDragPayload = payload;
  dataTransfer.setData(libraryDocumentDragMimeType, JSON.stringify(payload));
};

export const clearCopilotLibraryDocumentDragPayload = (): void => {
  currentLibraryDocumentDragPayload = undefined;
};

export const getCopilotLibraryDocumentDragPayload = (
  dataTransfer: DataTransfer,
): CopilotLibraryDocumentDragPayload | undefined => {
  const rawPayload = dataTransfer.getData(libraryDocumentDragMimeType);
  if (rawPayload) return parseCopilotLibraryDocumentDragPayload(rawPayload);

  return currentLibraryDocumentDragPayload;
};

const getMimeTypeFromFileName = (fileName: string): string | undefined => {
  const normalizedFileName = fileName.toLowerCase();
  const matchingExtension = Object.keys(copilotMarkdownExtractorExtensionMimeTypes).find((extension) =>
    normalizedFileName.endsWith(extension),
  );

  return matchingExtension ? copilotMarkdownExtractorExtensionMimeTypes[matchingExtension] : undefined;
};

export const isCopilotUploadDocumentNameSupported = (fileName: string): boolean => {
  const mimeType = getMimeTypeFromFileName(fileName);
  if (!mimeType) return false;

  return getCopilotMarkdownExtractorSourceFromMimeType(mimeType) !== undefined;
};

const isCopilotUploadDocumentMimeTypeSupported = (mimeType: string): boolean => {
  return getCopilotMarkdownExtractorSourceFromMimeType(mimeType) !== undefined;
};

const isCopilotUploadDocumentMimeTypeUnknown = (mimeType: string): boolean => {
  const normalizedMimeType = mimeType.trim().toLowerCase();

  return !normalizedMimeType || normalizedMimeType === 'application/octet-stream';
};

// a dot and 1 to 5 letters or digits, at least one of them a letter: "v3.0" carries no extension
const fileExtensionPattern = /\.(?=[0-9]*[a-z])[a-z0-9]{1,5}$/i;

// The drop check on a library document is a convenience that spares a round trip, not the authority: the
// server reads the file itself, takes its type from the drive item and answers with its own message when
// it cannot read it. Both names of the payload count, the file name of the link URL and the display name:
// the drop is supported when either is a name the extractors read; otherwise it is refused when either ends
// with an extension; a document whose names carry no extension at all is left to the server.
export const isCopilotLibraryDocumentPayloadSupported = (payload: CopilotLibraryDocumentDragPayload): boolean => {
  if (payload.listType === ResourceListType.SitePageLibrary) return true;
  if (payload.listType !== ResourceListType.DocumentLibrary) return false;

  const names = [...new Set([payload.fileName, payload.linkName])].filter((name): name is string => !!name);
  if (names.some((name) => isCopilotUploadDocumentNameSupported(name))) return true;

  return !names.some((name) => fileExtensionPattern.test(name));
};

const getCopilotUploadDocumentItems = (dataTransfer: DataTransfer): DataTransferItem[] => {
  return Array.from(dataTransfer.items ?? []).filter((item) => item.kind === 'file');
};

export const getCopilotUploadDocumentFiles = (dataTransfer: DataTransfer): File[] => {
  const files = Array.from(dataTransfer.files ?? []);
  if (files.length > 0) return files;

  return getCopilotUploadDocumentItems(dataTransfer)
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
};

export const isCopilotUploadDocumentFormatSupported = (file: File): boolean => {
  const normalizedMimeType = file.type.trim().toLowerCase();
  if (isCopilotUploadDocumentMimeTypeUnknown(normalizedMimeType)) {
    return isCopilotUploadDocumentNameSupported(file.name);
  }

  return isCopilotUploadDocumentMimeTypeSupported(normalizedMimeType);
};

const getAcceptedDropResult = (
  messageKey: CopilotDocumentDropValidationMessageKey,
): CopilotDocumentDropValidationResult => {
  return { handled: true, accepted: true, messageKey };
};

const getRejectedDropResult = (
  messageKey: CopilotDocumentDropValidationMessageKey,
): CopilotDocumentDropValidationResult => {
  return { handled: true, accepted: false, messageKey };
};

export const validateCopilotDocumentDrop = (
  dataTransfer: DataTransfer,
  libraryDocumentDropEnabled: boolean,
  uploadDocumentDropEnabled: boolean,
): CopilotDocumentDropValidationResult => {
  if (!hasCopilotLibraryDocumentDragPayload(dataTransfer)) {
    if (!Array.from(dataTransfer.types).includes('Files')) return { handled: false, accepted: false };
    if (!uploadDocumentDropEnabled) return getRejectedDropResult('DropUnavailable');

    const files = getCopilotUploadDocumentFiles(dataTransfer);
    if (files.length !== 1) return getRejectedDropResult('DropUploadMultiple');
    if (files[0].size > globalMaxUploadSize) return getRejectedDropResult('DropUploadTooLarge');
    if (!isCopilotUploadDocumentFormatSupported(files[0])) {
      return getRejectedDropResult('DropUploadUnsupported');
    }

    return getAcceptedDropResult('DropUploadAccept');
  }

  if (!libraryDocumentDropEnabled) return getRejectedDropResult('DropUnavailable');

  const payload = getCopilotLibraryDocumentDragPayload(dataTransfer);
  if (!payload) return getRejectedDropResult('DropInvalid');
  if (payload.listType === ResourceListType.SitePageLibrary) {
    return getAcceptedDropResult('DropAccept');
  }
  if (payload.listType !== ResourceListType.DocumentLibrary) return getRejectedDropResult('DropInvalid');
  if (!isCopilotLibraryDocumentPayloadSupported(payload)) {
    return getRejectedDropResult('DropUploadUnsupported');
  }

  return getAcceptedDropResult('DropAccept');
};

export const validateCopilotDocumentDragOver = (
  dataTransfer: DataTransfer,
  libraryDocumentDropEnabled: boolean,
  uploadDocumentDropEnabled: boolean,
): CopilotDocumentDropValidationResult => {
  if (hasCopilotLibraryDocumentDragPayload(dataTransfer)) {
    if (!libraryDocumentDropEnabled) return getRejectedDropResult('DropUnavailable');

    return getAcceptedDropResult('DropAccept');
  }

  if (!Array.from(dataTransfer.types).includes('Files')) return { handled: false, accepted: false };
  if (!uploadDocumentDropEnabled) return getRejectedDropResult('DropUnavailable');

  return getAcceptedDropResult('DropUploadAccept');
};
